import { ButtonGroup, Flex, Text, Tooltip } from "@chakra-ui/react";
import React from "react";
import { HiOutlineClipboardCopy } from "react-icons/hi";
import { HiOutlineLink } from "react-icons/hi2";

import {
  Button,
  IconButton,
  successToast,
  useToast,
} from "../../../components";
import { copy } from "../../../utils/clipboard";
import { escapeHtml } from "../../../utils/escape";
import { AiInterviewerFragment } from "../../graphql";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";

type AiInterviewerSettingsTestInterviewProps = {
  aiInterviewer: Pick<AiInterviewerFragment, "id" | "name">;
  wizardOn?: boolean;
};

export const AiInterviewerSettingsTestInterview: React.FC<
  AiInterviewerSettingsTestInterviewProps
> = ({ aiInterviewer, wizardOn }) => {
  const toast = useToast();
  const {
    formState: { isDirty },
    watch,
  } = useAiInterviewerFormContext();

  const jobDescription = watch("jobDescription");
  const questions = watch("questions");

  const missingFields: string[] = [];
  if (!jobDescription?.trim()) missingFields.push("a job description");
  if (!questions?.length) missingFields.push("guide questions");

  const isSetupIncomplete = missingFields.length > 0;
  const isDisabled = isDirty || isSetupIncomplete;

  const disabledTooltip = isSetupIncomplete
    ? `Add ${missingFields.join(" and ")} to test this interviewer`
    : "Save your changes to test this interviewer";

  const href = `${window.location.origin}/external/ai-interviewer/${aiInterviewer.id}`;

  const copyLink = () => {
    copy({
      plain: href,
      html: `<a href="${href}">${escapeHtml(
        aiInterviewer.name || "AI interviewer"
      )}—Test interview</a>`,
    });
    successToast(toast, "Link copied to clipboard");
  };

  if (wizardOn) {
    return (
      <>
        <Text fontSize="xl" fontWeight="600" color="gray.800">
          Test interview
        </Text>
        <Text fontSize="sm" color="gray.600" mt="2">
          Take the AI interview yourself or share it with a colleague.
        </Text>
        <Text fontSize="sm" color="gray.600" mt={1}>
          Note: Test links will only work for members of your BrightHire org.
        </Text>
        <Flex mt="4" gap={3} align="center">
          <Tooltip
            label={disabledTooltip}
            isDisabled={!isDisabled}
            openDelay={500}
          >
            <Button
              variant="outline"
              size="prismMd"
              isDisabled={isDisabled}
              aria-label="Take test interview"
              onClick={() => window.open(href, "_blank")}
            >
              Take test interview
            </Button>
          </Tooltip>
          <Tooltip label="Copy test interview link" openDelay={500}>
            <IconButton
              variant="ghost"
              size="sm"
              isDisabled={isDisabled}
              aria-label="Copy link"
              icon={<HiOutlineLink size={18} />}
              onClick={copyLink}
            />
          </Tooltip>
        </Flex>
      </>
    );
  }

  return (
    <>
      <Text fontSize="sm" color="gray.600">
        Test the AI interviewer yourself or share it with a colleague.{" "}
      </Text>
      <ButtonGroup mt="4" size="prismMd" spacing={4}>
        <Tooltip
          label={disabledTooltip}
          isDisabled={!isDisabled}
          openDelay={500}
        >
          <Button
            variant="solid"
            isDisabled={isDisabled}
            aria-label="Start test interview"
            onClick={() => {
              window.open(href, "_blank");
            }}
          >
            Start test interview
          </Button>
        </Tooltip>
        <Tooltip
          shouldWrapChildren
          label="Test interviews are only for logged in users. Candidates won't be able to use this link."
          isDisabled={isDisabled}
          openDelay={500}
        >
          <Button
            variant="outline"
            isDisabled={isDisabled}
            aria-label="Copy link"
            leftIcon={<HiOutlineClipboardCopy />}
            onClick={copyLink}
          >
            Copy link
          </Button>
        </Tooltip>
      </ButtonGroup>
    </>
  );
};
